import { useIsMobile } from "@/hooks/use-mobile";
import { formatAmount } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { getOwnerBomBase, routes } from "@/routes/utils";
import { useGetPaymentReport } from "@/services";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { BUDGET_YEAR, GL_ACCOUNT, POSTING_YEAR } from "./consts";
import moment from "moment";
import { Button } from "@/components/ui";
import { useLocation, useNavigate } from "react-router-dom";

const usePayment360Report = () => {
  const isMobile = useIsMobile();
  const fromDashboard = useLocation().pathname?.includes("/dashboard");
  const base = getOwnerBomBase(useLocation().pathname);
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [filterData, setFilterData] = useState<any>(null);
  const [tableData, setTableData] = useState<any>([]);
  const [selectedOption, setSelectedOption] = useState<string>(BUDGET_YEAR);
  const [summaryCard, setSummaryCard] = useState([
    {
      title: "#Budget (GL)",
      count: 0,
      amountTitle: "Expense Amount",
      amount: 0,
    },
    {
      title: "#Committed (GL)",
      count: 0,
      amountTitle: "Amount",
      amount: 0,
    },
    {
      title: "#Actual (GL)",
      count: 0,
      amountTitle: "Amount",
      amount: 0,
    },
  ]);
  const navigate = useNavigate();
  const {
    communityData: communityDetail,
    currentFinanceYear,
    glMaster
  } = useSelector((state: RootState) => state.currentData);
  const breadcrumbDataDashboard = useMemo(
    () => [
      {
        link: `/${base}/${routes.dashboard}`,
        name: `Dashboard`,
      },
      {
        link: `/${routes.bom}/${routes.finance}/${routes.payment}/${routes.reports}`,
        name: <span className="text-primary">Payment 360 Report</span>,
      },
    ],
    []
  );

  const breadcrumbDataFinance = useMemo(
    () => [
      {
        link: `/${routes.bom}/${routes.finance}`,
        name: `Activity Dashboard`,
      },
      {
        link: `/${routes.bom}/${routes.finance}/${routes.payment}`,
        name: <span>{"Payment"}</span>,
      },
      {
        link: `/${routes.bom}/${routes.finance}/${routes.payment}/${routes.reports}`,
        name: <span className="text-primary">Payment 360 Report</span>,
      },
    ],
    []
  );

  const breadcrumbData = fromDashboard
    ? breadcrumbDataDashboard
    : breadcrumbDataFinance;

  const {
    data: paymentReportData,
    isLoading: isLoadingReport,
    refetch: refetchPaymentReportData,
    isRefetching: isRefetchingPaymentReport,
  } = useGetPaymentReport({
    communityId: communityDetail?.communityId,
    data: {
      ...(selectedOption == POSTING_YEAR ? {[POSTING_YEAR]: currentFinanceYear} : {[BUDGET_YEAR]: currentFinanceYear}),
      communityId: communityDetail?.communityId,
      glMasterIds: (filterData as any)?.[GL_ACCOUNT]
        ? (filterData as any)?.[GL_ACCOUNT]
        : fromDashboard && glMaster
        ? glMaster
        : [],
      ...((filterData as any)?.POSTED_DATE_RANGE
        ? {
            postedStartDate: moment(
              (filterData as any)?.POSTED_DATE_RANGE?.startDate
            ).format("MM/DD/YYYY"),
            postedEndDate: moment(
              (filterData as any)?.POSTED_DATE_RANGE?.endDate
            ).format("MM/DD/YYYY"),
          }
        : {}),
      ...((filterData as any)?.PAYMENT_DATE_RANGE
        ? {
            paymentStartDate: moment(
              (filterData as any)?.PAYMENT_DATE_RANGE?.startDate
            ).format("MM/DD/YYYY"),
            paymentEndDate: moment(
              (filterData as any)?.PAYMENT_DATE_RANGE?.endDate
            ).format("MM/DD/YYYY"),
          }
        : {}),
    },
    queryParams: {
      enabled: !!communityDetail?.communityId,
    },
  });

  useEffect(() => {
    if (paymentReportData?.data) {
      const paymentData =
        paymentReportData?.data?.glAccountData?.map((data: any) => ({
          ...data,
          glAccount: [data?.glAccountNumber, data?.glAccount]
            .filter(Boolean)
            .join(" - "),
          children:
            data?.glAccountMonthlyData?.map((gl: any) => ({
              ...gl,
              glAccountId: data?.glAccountId,
            })) || [],
          id: data?.glAccountId,
        })) || [];

      if (JSON.stringify(paymentData) != JSON.stringify(tableData)) {
        setTableData(paymentData);
      }
    }
  }, [paymentReportData]);

  useEffect(() => {
    let budgetCount = 0,
      budgetAmount = 0,
      commitCount = 0,
      commitAmount = 0,
      actualCount = 0,
      actualAmt = 0;
    paymentReportData?.data?.glAccountData?.map((data: any) => {
      if (data.budgetAmount) {
        budgetCount += 1;
        budgetAmount += data?.budgetAmount;
      }
      if (data.committedAmount) {
        commitCount += 1;
        commitAmount += data.committedAmount;
      }
      if (data.actualAmount) {
        actualCount += 1;
        actualAmt += data.actualAmount;
      }
    });

    summaryCard[0].count = budgetCount;
    summaryCard[0].amount = budgetAmount;
    summaryCard[1].count = commitCount;
    summaryCard[1].amount = commitAmount;
    summaryCard[2].count = actualCount;
    summaryCard[2].amount = actualAmt;
    setSummaryCard(summaryCard);
  }, [paymentReportData]);

  // Define Table Columns
  const columns = [
    {
      accessorKey: "glAccount",
      header: <div>GL Account</div>,
    },
    {
      accessorKey: "budgetAmount",
      meta: { align: "right" },
      header: <div>Budget Amt.</div>,
      accessorFn: (row: any) => formatAmount(row?.budgetAmount || "0"),
    },
    {
      accessorKey: "committedAmount",
      meta: { align: "right" },
      header: <div>Committed Amt.</div>,
      accessorFn: (row: any) => formatAmount(row?.committedAmount || "0"),
    },
    {
      accessorKey: "varCom",
      header: <div>Var-Com</div>,
      accessorFn: (row: any) =>
        formatAmount(row?.budgetAmount - row?.committedAmount || "0"),
    },
    {
      accessorKey: "actualAmount",
      meta: { align: "right" },
      header: <div>Actuals</div>,
      accessorFn: (row: any) => formatAmount(row?.actualAmount || "0"),
    },
    {
      accessorKey: "varAct",
      header: <div>Var-Act</div>,
      accessorFn: (row: any) =>
        formatAmount(row?.budgetAmount - row?.actualAmount || "0"),
    },
  ];

  // Define Table Columns
  const subColumns = [
    {
      accessorKey: "monthName",
      header: <div>Month</div>,
    },
    {
      accessorKey: "budgetAmount",
      meta: { align: "right" },
      header: <div>Budget Amt.</div>,
      accessorFn: (row: any) => formatAmount(row?.budgetAmount || "0"),
    },
    {
      accessorKey: "committedAmount",
      meta: { align: "right" },
      header: <div>Committed Amt.</div>,
      accessorFn: (row: any) => {
        if (!row?.committedAmount || fromDashboard) {
          return formatAmount(row?.committedAmount || "0");
        } else {
          return (
            <Button
              variant="link"
              className="p-0 h-auto"
              onClick={() => {
                navigate(`${row.glAccountId}/${row.monthName}`);
              }}
            >
              {formatAmount(row?.committedAmount || "0")}
            </Button>
          );
        }
      },
    },
    {
      accessorKey: "actualAmount",
      meta: { align: "right" },
      header: <div>Actuals</div>,
      accessorFn: (row: any) => {
        if (!row?.actualAmount || fromDashboard) {
          return formatAmount(row?.actualAmount || "0");
        } else {
          return (
            <Button
              variant="link"
              className="p-0 h-auto"
              onClick={() => {
                navigate(
                  `${row.glAccountId}/${row.monthName}/${routes.listing}/all`
                );
              }}
            >
              {formatAmount(row?.actualAmount || "0")}
            </Button>
          );
        }
      },
    },
  ];

  const onCancelFilter = () => {
    setOpenFilter(false);
    refetchPaymentReportData();
  };

  const onFilterBtnClick = () => {
    setOpenFilter(true);
  };

  const onFilter = (data: any) => {
    setFilterData(data);
    setTimeout(() => refetchPaymentReportData(), 10);
  };

  const onBackClick = () => {
    fromDashboard
      ? navigate(`/${base}/${routes.dashboard}`)
      : navigate(`/${routes.bom}/${routes.finance}/${routes.payment}`);
  };

  const onOptionChanges = (value: string) => {
    setSelectedOption(value);
    setTimeout(() => refetchPaymentReportData(), 10);
  };

  return {
    breadcrumbData,
    isMobile,
    summaryCard,
    columns,
    openFilter,
    onCancelFilter,
    onFilterBtnClick,
    filterData,
    setFilterData,
    isLoadingReport,
    isRefetchingPaymentReport,
    tableData,
    refetchPaymentReportData,
    subColumns,
    onFilter,
    fromDashboard,
    onBackClick,
    currentFinanceYear,
    onOptionChanges,
    selectedOption
  };
};

export default usePayment360Report;
